// skuOptions.ts — product-picker options that work in both data modes.
//
// Mock rows join batches to products by product id. Real-mode batches key on
// SKU strings (the ProductMapping internal SKU), which never match snapshot
// ids — so in real mode the picker offers the SKUs that actually have stock,
// and downstream batch filters (`b.product === selected`) keep working.

import { useRealApi } from '@/api/paths';
import type { Batch, Product } from './types';

export interface PickerOption {
  value: string;
  label: string;
}

export function productPickerOptions(products: Product[], batches: Batch[]): PickerOption[] {
  if (!useRealApi) {
    return products
      .filter((p) => p.status === 'Active')
      .map((p) => ({ value: p.id, label: `${p.name} (${p.sku})` }));
  }
  // Real mode: show all active products from catalog. For stock-in, the user
  // may be receiving products that have no existing batches yet.
  if (products.length > 0) {
    return products
      .filter((p) => p.status === 'Active')
      .map((p) => ({ value: p.id, label: p.name || p.sku }));
  }
  // Fallback: if products haven't loaded yet, use batch SKUs
  const skus = [...new Set(batches.map((b) => b.product))].sort();
  return skus.map((sku) => ({ value: sku, label: sku }));
}

/** Resolve the SKU to submit for a picker selection (catalog product or raw SKU). */
export function pickedSku(products: Product[], selected: string): string {
  return products.find((p) => p.id === selected)?.sku ?? selected;
}

/**
 * Product-picker options limited to what the store actually HOLDS in stock — one option per
 * distinct in-stock product (value = the batch key `b.product`, i.e. the snapshot_key). Used by
 * flows that operate on existing stock (adjustments), where offering the full 185k catalogue is
 * confusing: the user would pick a product that has no batch to act on. Products not yet stocked
 * must be received first (Inward / Import) before they can be adjusted.
 */
export function inStockProductOptions(batches: Batch[], store: string): PickerOption[] {
  const byKey = new Map<string, string>(); // b.product (snapshot_key) -> display label
  for (const b of batches) {
    if (store !== 'ALL' && b.store !== store) continue;
    if ((b.qty ?? 0) <= 0) continue;
    if (!byKey.has(b.product)) byKey.set(b.product, b.displayName || b.product);
  }
  return [...byKey.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
